import { useEffect, useState, type FormEvent, type JSX } from "react";
import { Check, CirclePlus, X } from "lucide-react";
import {
  fxKontoAendern,
  fxKontoAnlegen,
  fxKontenLaden,
  type FxAccount,
} from "../../api/kurssicherung.ts";
import { KonfliktFehler } from "../../api/basis.ts";
import { nachladeFehlertext } from "../../lib/ausgangUnklar.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";

export interface FxKontenDialogProps {
  offen: boolean;
  rechte: ReadonlySet<string>;
  konten: readonly FxAccount[];
  onSchliessen: () => void;
  onMeldung: (text: string) => void;
}

interface KontoFormularProps {
  konto: FxAccount;
  kannVerwalten: boolean;
  letztesAktivesStandardkonto: boolean;
  onGespeichert: (konto: FxAccount, meldung: string) => void;
  onKonflikt: (stand: unknown) => void;
}

function KontoFormular({
  konto,
  kannVerwalten,
  letztesAktivesStandardkonto,
  onGespeichert,
  onKonflikt,
}: KontoFormularProps): JSX.Element {
  const [name, setName] = useState(konto.name);
  const [isActive, setIsActive] = useState(konto.isActive);
  const [fehler, setFehler] = useState("");
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    setName(konto.name);
    setIsActive(konto.isActive);
    setFehler("");
  }, [konto]);

  const unveraendert = name.trim() === konto.name && isActive === konto.isActive;

  function speichern(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const bereinigterName = name.trim();
    if (!bereinigterName) {
      setFehler("Ein Kontoname ist erforderlich.");
      return;
    }
    if (unveraendert) return;
    setFehler("");
    setSpeichert(true);
    fxKontoAendern(konto.id, konto.rowVersion, { name: bereinigterName, isActive })
      .then((aktualisiert) => onGespeichert(aktualisiert, "USD-Konto aktualisiert"))
      .catch((ursache: unknown) => {
        if (ursache instanceof KonfliktFehler) {
          onKonflikt(ursache.aktuellerStand);
          return;
        }
        // Inline bleibt inline (Spec R2): Text aus `fehlertext()`, der feste Satz ist der Kontext.
        setFehler(fehlertext(ursache, "Das USD-Konto konnte nicht aktualisiert werden.") ?? "");
      })
      .finally(() => setSpeichert(false));
  }

  return (
    <form className="fx-konto-formular" onSubmit={speichern}>
      <div className="fx-konto-name">
        {kannVerwalten ? (
          <label>
            Name
            <input
              value={name}
              disabled={speichert}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
        ) : (
          <span className="fx-konto-aktueller-name">{konto.name}</span>
        )}
        <span className="fx-konto-waehrung">USD</span>
      </div>
      <div className="fx-konto-optionen">
        <label>
          <input
            type="checkbox"
            checked={isActive}
            disabled={!kannVerwalten || speichert || letztesAktivesStandardkonto}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Aktiv
        </label>
      </div>
      {letztesAktivesStandardkonto ? (
        <p className="fx-konto-hinweis">Das letzte aktive Standardkonto kann nicht deaktiviert werden.</p>
      ) : null}
      {fehler ? <p className="fx-konto-fehler" role="alert">{fehler}</p> : null}
      {kannVerwalten ? (
        <div className="fx-konto-aktion">
          <button className="btn-sec" type="submit" disabled={speichert || unveraendert}>
            <Check aria-hidden="true" strokeWidth={1.8} /> Änderungen speichern
          </button>
        </div>
      ) : null}
    </form>
  );
}

export function FxKontenDialog({
  offen,
  rechte,
  konten: kontenProp,
  onSchliessen,
  onMeldung,
}: FxKontenDialogProps): JSX.Element | null {
  const [konten, setKonten] = useState<readonly FxAccount[]>(kontenProp);
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const [name, setName] = useState("");
  const [fehler, setFehler] = useState("");
  const [speichert, setSpeichert] = useState(false);
  const [nachladeFehler, setNachladeFehler] = useState("");
  const [laedtNeu, setLaedtNeu] = useState(false);
  const kannVerwalten = hatRecht(rechte, "manage_fx");

  useEffect(() => {
    setKonten(kontenProp);
  }, [kontenProp]);

  if (!offen) return null;

  function fxKontoPruefen(wert: unknown): wert is FxAccount {
    if (typeof wert !== "object" || wert === null) return false;
    const konto = wert as Partial<FxAccount>;
    return typeof konto.id === "string" &&
      typeof konto.name === "string" &&
      konto.currency === "USD" &&
      typeof konto.isActive === "boolean" &&
      typeof konto.isDefault === "boolean" &&
      typeof konto.rowVersion === "number";
  }

  function kontenNeuLaden(erfolgsMeldung = ""): void {
    setLaedtNeu(true);
    setNachladeFehler("");
    fxKontenLaden()
      .then((aktualisiert) => setKonten(aktualisiert))
      .catch((ursache: unknown) => {
        // Die Zeile mit dem Knopf "Konten aktualisieren" bleibt (R2); neu ist, dass die Ursache dahinter steht.
        // MIT Erfolgssatz (R4: die Aktion gelang, nur das Nachladen scheiterte) steht sie nur, wenn sie nicht zum
        // Wiederholen raet (R-Z2: hinter "Das USD-Konto wurde angelegt." waere "erneut versuchen" der Weg in ein zweites
        // Konto). OHNE Erfolgssatz ist es ein reiner Ladeweg - der Knopf "Konten aktualisieren" und der 409-Weg, in dem
        // nichts gespeichert wurde: dort ist der Rat harmlos und die Ursache gehoert dazu (R-Z15). `null` (Abbruch)
        // laesst den Satz allein stehen.
        const satz = erfolgsMeldung
          ? `${erfolgsMeldung} Die Kontenliste konnte nicht aktualisiert werden.`
          : "Die Kontenliste konnte nicht aktualisiert werden.";
        setNachladeFehler((erfolgsMeldung ? nachladeFehlertext(ursache, satz) : fehlertext(ursache, satz)) ?? satz);
      })
      .finally(() => setLaedtNeu(false));
  }

  function nachSpeichern(aktualisiert: FxAccount, meldung: string): void {
    setKonten((vorher) => {
      const vorhanden = vorher.some((konto) => konto.id === aktualisiert.id);
      return vorhanden
        ? vorher.map((konto) => konto.id === aktualisiert.id ? aktualisiert : konto)
        : [...vorher, aktualisiert];
    });
    setAnlegenOffen(false);
    setName("");
    onMeldung(meldung);
    kontenNeuLaden(`Das USD-Konto wurde ${meldung === "USD-Konto angelegt" ? "angelegt" : "aktualisiert"}.`);
  }

  function konfliktUebernehmen(stand: unknown): void {
    if (fxKontoPruefen(stand)) {
      setKonten((vorher) => vorher.map((konto) => konto.id === stand.id ? stand : konto));
      // Die Aenderung wurde NICHT gespeichert (409): Stapel statt 2,2-s-Toast, damit der Nutzer es liest.
      meldeFehler("USD-Konto wurde zwischenzeitlich geändert. Serverstand übernommen.");
      return;
    }
    meldeFehler("USD-Konto wurde zwischenzeitlich geändert. Kontenliste wird aktualisiert.");
    kontenNeuLaden();
  }

  function kontoAnlegen(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const bereinigterName = name.trim();
    if (!bereinigterName) {
      setFehler("Ein Kontoname ist erforderlich.");
      return;
    }
    setFehler("");
    setSpeichert(true);
    fxKontoAnlegen({ name: bereinigterName, currency: "USD" })
      .then((aktualisiert) => nachSpeichern(aktualisiert, "USD-Konto angelegt"))
      .catch((ursache: unknown) => {
        setFehler(fehlertext(ursache, "Das USD-Konto konnte nicht angelegt werden.") ?? "");
      })
      .finally(() => setSpeichert(false));
  }

  return (
    <div className="board-editor-overlay open" role="presentation">
      <section className="board-editor-dialog fx-konten-dialog" role="dialog" aria-modal="true" aria-labelledby="fxKontenTitel">
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="fxKontenTitel">USD-Konten verwalten</h2>
            <p>Weitere Konten sind vorbereitend. Der Standardkontowechsel ist deaktiviert, solange das Sammelkonto genutzt wird.</p>
          </div>
          <button className="icon-btn" type="button" onClick={onSchliessen} title="Dialog schließen">
            <X aria-hidden="true" strokeWidth={1.8} />
          </button>
        </div>
        <div className="board-editor-body fx-konten-inhalt">
          <div className="fx-konten-liste">
            {konten.length === 0 ? <p className="fx-konto-hinweis">Keine USD-Konten vorhanden</p> : null}
            {konten.map((konto) => (
              <KontoFormular
                key={konto.id}
                konto={konto}
                kannVerwalten={kannVerwalten}
                letztesAktivesStandardkonto={konto.isActive && konto.isDefault}
                onGespeichert={nachSpeichern}
                onKonflikt={konfliktUebernehmen}
              />
            ))}
          </div>
          {kannVerwalten && anlegenOffen ? (
            <form className="fx-konto-neu" onSubmit={kontoAnlegen}>
              <label>
                Name
                <input value={name} onChange={(event) => setName(event.target.value)} disabled={speichert} autoFocus />
              </label>
              {fehler ? <p className="fx-konto-fehler" role="alert">{fehler}</p> : null}
              <div className="fx-konto-aktion">
                <button className="btn-sec" type="button" onClick={() => setAnlegenOffen(false)} disabled={speichert}>
                  <X aria-hidden="true" strokeWidth={1.8} /> Abbrechen
                </button>
                <button className="btn-primary" type="submit" disabled={speichert}>
                  <Check aria-hidden="true" strokeWidth={1.8} /> Speichern
                </button>
              </div>
            </form>
          ) : null}
          {kannVerwalten && !anlegenOffen ? (
            <button className="board-editor-add" type="button" onClick={() => setAnlegenOffen(true)}>
              <CirclePlus aria-hidden="true" strokeWidth={1.8} /> Konto anlegen
            </button>
          ) : null}
          {nachladeFehler ? (
            <div className="fx-konto-nachladefehler" role="alert">
              <span>{nachladeFehler}</span>
              <button className="btn-sec" type="button" onClick={() => kontenNeuLaden()} disabled={laedtNeu}>
                Konten aktualisieren
              </button>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
